import React, { FC, useEffect, useState } from 'react';
import { Button, Checkbox, Drawer, Form, Input } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { WarningCard } from 'common/components';
import { EMode } from 'common/const';
import { numberToString, renderRequiredMark, rules, showErrorMessage, showSuccessMessage } from 'common/helpers';
import { useLegalEntityContext } from 'common/context';
import { LEGAL_INN_LENGTH_10, LEGAL_INN_LENGTH_12, LEGAL_KPP_LENGTH } from 'common/config';
import { IFormValue } from 'common/models';
import { Close } from 'common/svg';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { ICreateLegalPayload, IUpdateLegalPayload } from 'entities/LegalEntity/LegalEntity.models';
import {
  createLegal,
  selectIsLegalLoading,
  selectLegal,
  selectLegalError,
  setLegalError,
  updateLegal,
} from 'entities/LegalEntity/Legal.slice';

export const EditLegalEntityCard: FC = () => {
  const dispatch = useAppDispatch();

  const legal = useAppSelector(selectLegal);
  const isLegalLoading = useAppSelector(selectIsLegalLoading);
  const legalError = useAppSelector(selectLegalError);

  const [openWarningCard, setOpenWarningCard] = useState<boolean>(false);
  const [formIsChanged, setFormIsChanged] = useState<boolean>(false);
  const [payload, setPayload] = useState<IUpdateLegalPayload | null>(null);
  const [form] = useForm();
  const { editLegalEntityCardMode, openEditLegalEntityCard, setOpenEditLegalEntityCard } = useLegalEntityContext();

  const isEditMode = editLegalEntityCardMode === EMode.Edit;

  useEffect(() => {
    if (openEditLegalEntityCard && legalError) {
      showErrorMessage(legalError);
    } else {
      dispatch(setLegalError(null));
    }
  }, [openEditLegalEntityCard, legalError]);

  const onClose = () => {
    setFormIsChanged(false);
    setOpenWarningCard(false);
    setOpenEditLegalEntityCard(false);
  };

  const handleClose = () => {
    if (isLegalLoading) {
      return;
    }

    if (formIsChanged) {
      setOpenEditLegalEntityCard(false);
      setOpenWarningCard(true);
      return;
    }

    onClose();
  };

  const onFormValuesChange = (value: IFormValue) => {
    setFormIsChanged(true);
    setPayload((prev) => (prev ? { ...prev, ...value } : null));
  };

  const onCreateSubmit = (value: ICreateLegalPayload) => {
    dispatch(setLegalError(null));
    dispatch(
      createLegal({
        ...value,
        ...(value.inn.length === LEGAL_INN_LENGTH_12 && { kpp: null }),
        onSuccess: () => {
          showSuccessMessage('Новое юридическое лицо добавлено в аккаунт.');
          onClose();
        },
      }),
    );
  };

  const onUpdateSubmit = () => {
    dispatch(setLegalError(null));

    if (!payload) {
      return;
    }

    dispatch(
      updateLegal({
        ...payload,
        ...(payload.inn && payload.inn.length === LEGAL_INN_LENGTH_12 && { kpp: null }),
        onSuccess: () => {
          showSuccessMessage('Реквизиты юридического лица успешно изменены.');
          onClose();
        },
      }),
    );
  };

  useEffect(() => {
    if (openEditLegalEntityCard) {
      if (isEditMode && legal) {
        setPayload({ id: legal.id });
        form.setFieldsValue({
          name: legal.name,
          inn: legal.inn,
          kpp: legal.kpp,
        });
      } else {
        form.resetFields();
      }
    }
  }, [openEditLegalEntityCard, isEditMode, legal]);

  return (
    <>
      <Drawer
        rootClassName="redesign drawer edit-legal-entity-card"
        destroyOnClose
        width={720}
        open={openEditLegalEntityCard}
        onClose={handleClose}
        footer={
          isEditMode ? (
            <Button className="button-lg primary" loading={isLegalLoading} onClick={form.submit}>
              Сохранить изменения
            </Button>
          ) : (
            <Button className="button-lg primary" loading={isLegalLoading} onClick={form.submit}>
              Добавить юр. лицо
            </Button>
          )
        }
      >
        <div className="drawer__body">
          <div className="drawer__title mb-14">{isEditMode ? 'Редактировать юр. лицо' : 'Новое юридическое лицо'}</div>

          <Form
            form={form}
            layout="vertical"
            requiredMark={renderRequiredMark}
            initialValues={{ isVatPayer: true }}
            onFinish={isEditMode ? onUpdateSubmit : onCreateSubmit}
            onValuesChange={onFormValuesChange}
          >
            <Form.Item className="mb-9" label="Название" name="name" rules={[rules.required()]}>
              <Input placeholder="Введите название" allowClear={{ clearIcon: <Close className="icon-close-input" /> }} />
            </Form.Item>

            <Form.Item
              className="mb-9"
              label="ИНН"
              name="inn"
              rules={[
                rules.required(),
                () => ({
                  validator(_, value) {
                    const inn = value ? numberToString(value) : undefined;

                    if (inn && inn.length !== LEGAL_INN_LENGTH_10 && inn.length !== LEGAL_INN_LENGTH_12) {
                      return Promise.reject(new Error('ИНН должен содержать 10 (для юридических лиц) или 12 (для ИП) цифр'));
                    }

                    return Promise.resolve();
                  },
                }),
              ]}
              normalize={(v) => (v === null ? '' : String(v).replace(/\D+/g, ''))}
            >
              <Input placeholder="Введите ИНН" />
            </Form.Item>

            <Form.Item
              className="mb-9"
              label="КПП (обязателен для юридических лиц)"
              name="kpp"
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const innValue = getFieldValue('inn');
                    const inn = innValue ? innValue.toString() : undefined;
                    const kpp = value ? value.toString() : undefined;

                    if (inn?.length === LEGAL_INN_LENGTH_10 && kpp?.length !== LEGAL_KPP_LENGTH) {
                      return Promise.reject(new Error('Для ИНН юридического лица КПП обязателен и должен содержать 9 символов'));
                    }

                    if (inn?.length === LEGAL_INN_LENGTH_12 && kpp) {
                      return Promise.reject(new Error('Для ИНН из 12 символов КПП должен быть пустым'));
                    }

                    return Promise.resolve();
                  },
                }),
              ]}
              normalize={(v) => (v === null ? '' : String(v).replace(/\D+/g, ''))}
            >
              <Input placeholder="Введите КПП" />
            </Form.Item>

            {!isEditMode && (
              <Form.Item name="isVatPayer" valuePropName="checked">
                <Checkbox>Плательщик НДС</Checkbox>
              </Form.Item>
            )}
          </Form>
        </div>
      </Drawer>

      <WarningCard
        open={openWarningCard}
        content="Вы уверены, что не хотите сохранить изменения?"
        subtitle="Введенные данные будут потеряны."
        confirmBtnTitle="Не сохранять"
        onConfirm={onClose}
        onClose={() => {
          setOpenWarningCard(false);
          setOpenEditLegalEntityCard(true);
        }}
      />
    </>
  );
};
